import React, { useState, useEffect, useCallback, useMemo } from 'react';
import worksheetService from '../../services/worksheetService';
import WorksheetFilterPanel from './WorksheetFilterPanel';
import WorksheetWorklistGrid from './WorksheetWorklistGrid';
import WorksheetDetailDrawer from './WorksheetDetailDrawer';
import WorksheetBatchModal from './WorksheetBatchModal';
import WorksheetPrintModal from './WorksheetPrintModal';
import WorksheetAuditModal from './WorksheetAuditModal';
import './WorksheetWorkbench.css';

const PathologyWorksheetPage = () => {
  // Filter & Search State
  const [filters, setFilters] = useState({
    fromDate: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d.toISOString().split('T')[0];
    })(),
    toDate: new Date().toISOString().split('T')[0],
    subCategoryId: null,
    priority: null,
    status: null,
    testName: '',
    searchTerm: '',
    tatRiskOnly: false,
    pageNumber: 1,
    pageSize: 25
  });

  const [isFilterCollapsed, setIsFilterCollapsed] = useState(false);

  // Data State
  const [worklistData, setWorklistData] = useState({
    items: [],
    totalCount: 0,
    totalPages: 1
  });
  const [telemetryMap] = useState({});
  const [analyzers, setAnalyzers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Load analyzers and templates once on mount
  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [analyzerRes, templateRes] = await Promise.all([
          worksheetService.getAnalyzers(),
          worksheetService.getTemplates()
        ]);
        if (analyzerRes.success && analyzerRes.data) {
          setAnalyzers(analyzerRes.data);
        }
        if (templateRes.success && templateRes.data) {
          setTemplates(templateRes.data);
        }
      } catch (err) {
        console.error('Failed to load worksheet masters:', err);
      }
    };
    fetchMasters();
  }, []);

  // Selection & Modal States
  const [selectedTestIds, setSelectedTestIds] = useState([]);
  const [activeDetailTestId, setActiveDetailTestId] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [printTestIds, setPrintTestIds] = useState([]);
  const [auditEntityId, setAuditEntityId] = useState(null);
  const [uncollectedAlert, setUncollectedAlert] = useState(null);

  // Fetch Worklist Data
  const fetchWorklist = useCallback(async () => {
    setIsLoading(true);
    try {
      const queryParams = { ...filters };
      if (!queryParams.searchTerm && queryParams.testName) {
        queryParams.searchTerm = queryParams.testName;
      }
      const res = await worksheetService.getWorklist(queryParams);
      if (res.success && res.data) {
        const items = res.data.items || [];
        setWorklistData({
          items: items,
          totalCount: res.data.totalCount || 0,
          totalPages: res.data.totalPages || 1
        });
      }
    } catch (err) {
      console.error('Failed to load worksheet list:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchWorklist();
  }, [fetchWorklist]);

  // Fetch Detail Data when drawer opens
  useEffect(() => {
    if (!activeDetailTestId) {
      setDetailData(null);
      return;
    }

    let isMounted = true;
    const fetchDetail = async () => {
      setIsDetailLoading(true);
      try {
        const res = await worksheetService.getWorkDetail(activeDetailTestId);
        if (isMounted && res.success && res.data) {
          setDetailData(res.data);
        }
      } catch (err) {
        console.error('Failed to load specimen work detail:', err);
      } finally {
        if (isMounted) setIsDetailLoading(false);
      }
    };

    fetchDetail();
    return () => {
      isMounted = false;
    };
  }, [activeDetailTestId]);

  // Reset Filters
  const handleResetFilters = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    setFilters({
      fromDate: d.toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      subCategoryId: null,
      priority: null,
      status: null,
      testName: '',
      searchTerm: '',
      tatRiskOnly: false,
      pageNumber: 1,
      pageSize: 25
    });
  };

  // Selection handlers
  const handleSelectTest = (testId) => {
    setSelectedTestIds((prev) =>
      prev.includes(testId) ? prev.filter((id) => id !== testId) : [...prev, testId]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const pageIds = worklistData.items.map((i) => i.testId);
      setSelectedTestIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    } else {
      const pageIds = worklistData.items.map((i) => i.testId);
      setSelectedTestIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    }
  };

  // Bench Workflow Actions
  const handleStartWork = async (testId) => {
    try {
      await worksheetService.startWork({ testIds: [testId] });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        const res = await worksheetService.getWorkDetail(testId);
        if (res.success) setDetailData(res.data);
      }
    } catch (err) {
      alert('Failed to start testing: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleHoldWork = async (testId, reason, remarks) => {
    try {
      await worksheetService.holdWork({ testId, reason, remarks });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        const res = await worksheetService.getWorkDetail(testId);
        if (res.success) setDetailData(res.data);
      }
    } catch (err) {
      alert('Failed to hold specimen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleResumeWork = async (testId) => {
    try {
      await worksheetService.resumeWork({ testId });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        const res = await worksheetService.getWorkDetail(testId);
        if (res.success) setDetailData(res.data);
      }
    } catch (err) {
      alert('Failed to resume specimen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRepeatWork = async (testId, reason, remarks) => {
    try {
      await worksheetService.repeatWork({ testId, reason, remarks });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        const res = await worksheetService.getWorkDetail(testId);
        if (res.success) setDetailData(res.data);
      }
    } catch (err) {
      alert('Failed to schedule repeat: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleRejectWork = async (testId, reason, remarks) => {
    try {
      await worksheetService.rejectWork({ testId, reason, remarks });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        const res = await worksheetService.getWorkDetail(testId);
        if (res.success) setDetailData(res.data);
      }
    } catch (err) {
      alert('Failed to reject specimen: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleCompleteWork = async (testId) => {
    if (!window.confirm('Mark this specimen testing as completed?')) return;
    try {
      await worksheetService.completeWork({ testIds: [testId] });
      fetchWorklist();
      if (activeDetailTestId === testId) {
        setActiveDetailTestId(null);
      }
    } catch (err) {
      alert('Failed to complete work: ' + (err.response?.data?.message || err.message));
    }
  };

  // Batch Creation
  const handleCreateBatch = async (batchPayload) => {
    const res = await worksheetService.createBatch(batchPayload);
    fetchWorklist();
    setSelectedTestIds([]);
    return res.data;
  };

  // Print Handlers
  const handleOpenPrintModal = (testIds) => {
    if (!testIds || testIds.length === 0) {
      alert('Please select at least one specimen to print.');
      return;
    }

    // Check if any selected specimens are NOT yet collected
    const uncollected = worklistData.items.filter(
      (i) => testIds.includes(i.testId) && (i.sampleStage === 'NOT_COLLECTED' || i.isSampleCollected === 'N')
    );

    if (uncollected.length > 0) {
      setUncollectedAlert({
        uncollectedItems: uncollected,
        allTestIds: testIds
      });
      return;
    }

    setPrintTestIds(testIds);
    setIsPrintModalOpen(true);
  };

  const handlePrintSingle = (testId) => {
    handleOpenPrintModal([testId]);
  };

  const handlePrintSelected = () => {
    if (selectedTestIds.length === 0) {
      alert('Please select specimens using the checkboxes.');
      return;
    }
    handleOpenPrintModal(selectedTestIds);
  };

  const handlePrintAllVisible = () => {
    const allIds = worklistData.items.map((i) => i.testId);
    if (allIds.length === 0) {
      alert('No specimens visible to print.');
      return;
    }
    handleOpenPrintModal(allIds);
  };

  const displayedItems = useMemo(() => {
    let list = worklistData.items || [];
    if (filters.testName && filters.testName.trim()) {
      const q = filters.testName.trim().toLowerCase();
      list = list.filter((item) =>
        (item.investigationName && item.investigationName.toLowerCase().includes(q)) ||
        (item.itemName && item.itemName.toLowerCase().includes(q))
      );
    }
    return list;
  }, [worklistData.items, filters.testName]);

  return (
    <div className="ws-container">
      {/* 1. Top Enterprise Header */}
      <header className="ws-header">
        <div className="ws-header-title">
          <span className="ws-header-badge">Clinical Workbench</span>
          <div>
            <h1 className="ws-header-h1">
              <span>🩸 SMART WORKSHEET</span>
              <span style={{ fontSize: '14px', fontWeight: 500, color: '#64748b' }}>
                Pathology → Technician Workbench
              </span>
            </h1>
            <p className="ws-header-subtitle">
              Real-time specimen accessioning, bench testing, TAT tracking, and dynamic print engine
            </p>
          </div>
        </div>

        <div className="ws-header-actions">
          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={() => setIsFilterCollapsed((prev) => !prev)}
            title="Toggle Filter Sidebar"
          >
            🔍 {isFilterCollapsed ? 'Show Filters' : 'Hide Filters'}
          </button>

          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={fetchWorklist}
            title="Refresh specimens list"
          >
            🔄 Refresh
          </button>

          {selectedTestIds.length > 0 && (
            <button
              type="button"
              className="ws-btn ws-btn-primary"
              onClick={() => setIsBatchModalOpen(true)}
              title="Create Batch from selected specimens"
            >
              🔬 Create Batch ({selectedTestIds.length})
            </button>
          )}

          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={() => window.location.href = '/masters/worksheet-templates'}
            title="Configure worksheet layouts & templates"
          >
            ⚙️ Template Master
          </button>

          <button
            type="button"
            className="ws-btn ws-btn-outline"
            onClick={handlePrintSelected}
            disabled={selectedTestIds.length === 0}
            title="Print selected specimens as PDF"
          >
            🖨️ Print Selected ({selectedTestIds.length})
          </button>

          <button
            type="button"
            className="ws-btn ws-btn-primary"
            style={{ fontWeight: 800, padding: '8px 16px', background: '#0284c7' }}
            onClick={handlePrintAllVisible}
            title="Print all visible specimens on this page or export as PDF"
          >
            🖨️ Print / Export PDF Worksheet
          </button>
        </div>
      </header>

      {/* 2. Top Filter Workbench (Collapsible) */}
      <WorksheetFilterPanel
        filters={filters}
        onChange={setFilters}
        onReset={handleResetFilters}
        isCollapsed={isFilterCollapsed}
        onToggleCollapse={() => setIsFilterCollapsed(true)}
      />

      {/* 3. Main Body: Specimen Queue Worklist Table */}
      <div className="ws-main-body">
        {/* Content Area */}
        <main className="ws-content-area">
          {/* Worklist Action Toolbar */}
          <div className="ws-toolbar">
            <div className="ws-toolbar-left">
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#334155' }}>
                Specimen Bench Queue
              </span>
              <span className="ws-badge" style={{ background: '#f1f5f9', color: '#475569' }}>
                {worklistData.totalCount} Specimens
              </span>
              {selectedTestIds.length > 0 && (
                <span className="ws-badge" style={{ background: '#e0f2fe', color: '#0369a1' }}>
                  {selectedTestIds.length} Selected
                </span>
              )}
            </div>

            <div className="ws-toolbar-right">
              {selectedTestIds.length > 0 && (
                <button
                  type="button"
                  className="ws-btn ws-btn-outline ws-btn-sm"
                  onClick={() => setSelectedTestIds([])}
                >
                  Clear Selection
                </button>
              )}
              <span style={{ fontSize: '12px', color: '#64748b' }}>
                Auto-sort: <strong>Priority (STAT first) ➔ TAT Remaining</strong>
              </span>
            </div>
          </div>

          {/* Worklist Data Grid */}
          <WorksheetWorklistGrid
            items={displayedItems}
            totalCount={filters.testName ? displayedItems.length : worklistData.totalCount}
            pageNumber={filters.pageNumber}
            pageSize={filters.pageSize}
            totalPages={worklistData.totalPages}
            onPageChange={(page) => setFilters((prev) => ({ ...prev, pageNumber: page }))}
            onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: newSize, pageNumber: 1 }))}
            isLoading={isLoading}
            selectedTestIds={selectedTestIds}
            telemetryMap={telemetryMap}
            onSelectTest={handleSelectTest}
            onSelectAll={handleSelectAll}
            onOpenDetail={(testId) => setActiveDetailTestId(testId)}
            onStartWork={handleStartWork}
            onHoldWork={(testId) => handleHoldWork(testId, 'Waiting for QC', '')}
            onResumeWork={handleResumeWork}
            onCompleteWork={handleCompleteWork}
            onPrintSingle={handlePrintSingle}
            onViewAudit={(testId) => setAuditEntityId(testId)}
          />
        </main>
      </div>

      {/* 4. Slide-over Work Detail Drawer */}
      <WorksheetDetailDrawer
        isOpen={!!activeDetailTestId}
        onClose={() => setActiveDetailTestId(null)}
        detail={detailData}
        isLoading={isDetailLoading}
        onStartWork={handleStartWork}
        onHoldWork={handleHoldWork}
        onResumeWork={handleResumeWork}
        onRepeatWork={handleRepeatWork}
        onRejectWork={handleRejectWork}
        onCompleteWork={handleCompleteWork}
        onPrintSingle={handlePrintSingle}
      />

      {/* 5. Batch Creation Modal */}
      <WorksheetBatchModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        selectedTestIds={selectedTestIds}
        selectedItems={worklistData.items.filter((i) => selectedTestIds.includes(i.testId))}
        analyzers={analyzers}
        onCreateBatch={handleCreateBatch}
        onPrintBatch={(batchId, ids) => handleOpenPrintModal(ids)}
      />

      {/* 6. Dynamic Print & PDF Preview Modal */}
      <WorksheetPrintModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        initialTestIds={printTestIds}
        availableTemplates={templates}
      />

      {/* 7. Worksheet Audit History Modal */}
      <WorksheetAuditModal
        isOpen={!!auditEntityId}
        onClose={() => setAuditEntityId(null)}
        entityId={auditEntityId}
        entityType="WorkItem"
      />

      {/* 8. Uncollected Specimen Warning Guard Modal */}
      {uncollectedAlert && (
        <div className="ws-modal-overlay" onClick={() => setUncollectedAlert(null)}>
          <div
            className="ws-modal-card"
            style={{ width: '600px', borderTop: '5px solid #dc2626' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ws-modal-header" style={{ background: '#fffbeb', borderBottom: '1px solid #fef3c7' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '26px' }}>⚠️</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', color: '#92400e', fontWeight: 800 }}>
                    Sample Not Collected Alert
                  </h3>
                  <div style={{ fontSize: '12px', color: '#b45309' }}>
                    Yeh sample abhi phlebotomy counter par collect nahi hua hai
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="ws-btn ws-btn-outline ws-btn-sm"
                onClick={() => setUncollectedAlert(null)}
              >
                ✕
              </button>
            </div>

            <div className="ws-modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ background: '#fef3c7', padding: '10px 14px', borderRadius: '6px', border: '1px solid #fde68a', fontSize: '13px', color: '#92400e' }}>
                ⚠️ <strong>Notice:</strong> Niche diye gaye <strong>{uncollectedAlert.uncollectedItems.length}</strong> specimen(s) abhi tak Phlebotomy me collect nahi hue hain.
                <br />
                Agar aap preliminary work ya manual entry ke liye abhi print karna chahte hain, toh <strong>'Continue &amp; Print'</strong> par click karein.
              </div>

              <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid #fed7aa', borderRadius: '6px', background: '#fffaf5' }}>
                <table className="ws-table" style={{ fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#ffedd5' }}>
                      <th style={{ color: '#9a3412' }}>Lab No</th>
                      <th style={{ color: '#9a3412' }}>Patient Name</th>
                      <th style={{ color: '#9a3412' }}>Investigation</th>
                      <th style={{ color: '#9a3412' }}>Container</th>
                      <th style={{ color: '#9a3412' }}>Collection Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {uncollectedAlert.uncollectedItems.map((item) => (
                      <tr key={item.testId}>
                        <td style={{ fontWeight: 700 }}>{item.labNo}</td>
                        <td>{item.patientName}</td>
                        <td>{item.testName}</td>
                        <td>{item.sampleType}</td>
                        <td>
                          <span className="ws-badge badge-hold" style={{ fontSize: '11px' }}>
                            🔴 Draw Pending
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="ws-modal-footer">
              <button
                type="button"
                className="ws-btn ws-btn-outline"
                onClick={() => setUncollectedAlert(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="ws-btn ws-btn-primary"
                onClick={() => {
                  const targetIds = uncollectedAlert.allTestIds;
                  setUncollectedAlert(null);
                  setPrintTestIds(targetIds);
                  setIsPrintModalOpen(true);
                }}
              >
                🖨️ Continue &amp; Print
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PathologyWorksheetPage;
